import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';

import Button from '@/components/Button';
import Dialog, {
	DialogContent,
	DialogDescription,
	DialogFooter,
	DialogHeader,
	DialogTitle
} from '@/components/Dialog';
import VisuallyHidden from '@/components/VisuallyHidden';
import { useIsTrialing } from '@/hooks/user';
import Clock from '@/icons/clock.svg';
import Limited1x from '@/images/limited/img-mobile-1x.png';
import Limited2x from '@/images/limited/img-mobile-2x.png';
import { BILLING_TAB } from '@/pages/Account/constants';
import routes from '@/routes';
import { setLimitedDialog } from '@/store/myDocuments';
import { AUTH_MODAL_TYPES, openAuthModal } from '@/store/profile';

function DailyLimitModal() {
	const { t } = useTranslation();
	const navigate = useNavigate();

	const loggedIn = useSelector(state => state.profile.loggedIn);

	const isTrialing = useIsTrialing();

	const openLimitedDialog = useSelector(
		state => state.myDocuments.limitedDialog
	);

	const dispatch = useDispatch();

	const messages = {
		guest: {
			subtitle: t('myDocuments.limitedPlan.dialog.subtitle'),
			text: t('myDocuments.limitedPlan.dialog.guestText'),
			button: t('myDocuments.limitedPlan.guestButton')
		},
		trial: {
			subtitle: t('myDocuments.limitedPlan.dialog.subtitle'),
			text: t('myDocuments.limitedPlan.dialog.text'),
			button: t('myDocuments.limitedPlan.button')
		}
	};

	const currentMessages = messages?.[isTrialing ? 'trial' : 'guest'];

	const handleOpenChange = value => dispatch(setLimitedDialog(value));

	const handleClose = () => handleOpenChange(false);

	return (
		<Dialog open={openLimitedDialog} onOpenChange={handleOpenChange}>
			<DialogContent className="w-[280px] p-0 md:w-[397px]">
				<form onSubmit={() => {}}>
					<DialogHeader className="border-b border-grey-200 px-4 py-3 text-left text-xs font-semibold leading-6 md:text-sm">
						{t('myDocuments.limitedPlan.dialog.title')}
					</DialogHeader>
					<VisuallyHidden>
						<DialogTitle>
							{t('myDocuments.limitedPlan.title')}
						</DialogTitle>
						<DialogDescription>
							{currentMessages.subtitle}
						</DialogDescription>
					</VisuallyHidden>
					{loggedIn ? (
						<div className="m-4 flex flex-col items-start rounded-md bg-red-background px-5 md:m-4 md:flex-row md:items-end">
							<div className="mr-2 flex flex-col gap-2 py-6">
								<Clock className="h-5 w-5 text-red" />
								<h3 className="text-xs font-semibold leading-5">
									{currentMessages.subtitle}
								</h3>
								<p className="text-xs leading-5">
									{currentMessages.text}
								</p>
							</div>
							<img
								className=""
								alt={t('myDocuments.limitedPlan.title')}
								src={Limited1x}
								srcSet={`${Limited1x} 1x, ${Limited2x} 2x`}
								width="130"
								height="131"
							/>
						</div>
					) : (
						<div className="px-4 py-6">
							<p className="text-xs leading-5 text-gray-500">
								{currentMessages.text}
							</p>
						</div>
					)}

					<DialogFooter className="flex flex-col sm:flex-col">
						<Button
							type="button"
							onClick={() => {
								handleClose();

								if (loggedIn) {
									return navigate(routes.myAccount, {
										state: {
											openChangePlanModal: isTrialing,
											currentTab: BILLING_TAB
										}
									});
								}

								dispatch(
									openAuthModal({
										type: AUTH_MODAL_TYPES.SIGN_UP,
										redirect: routes.checkout,
										scope: 'subscribe'
									})
								);
							}}
						>
							{currentMessages.button}
						</Button>
						{loggedIn && (
							<Button
								type="button"
								variant="outline"
								onClick={handleClose}
							>
								{t('common.cancel')}
							</Button>
						)}
					</DialogFooter>
				</form>
			</DialogContent>
		</Dialog>
	);
}

export default DailyLimitModal;
